import { isSet } from '../utils/isSet';

/**
 * Description: Depot Finder Google Maps Setup
 * 
 * @class DepotFinder
 * @typedef {DepotFinder}
 */

class DepotFinder {
    constructor (domNode, MapLib, MarkerLib, InfoWindowLib) {
        this.map;

        this.domNode = domNode;
        this.dataUrl = domNode.getAttribute("data-fetch-url");
        this.mapElm = this.domNode.querySelector("#map");
        this.searchCol = this.domNode.querySelector(".depot-finder__depots");
        this.zoomOutControlElm = this.domNode.querySelector(".map-controls__zoom-out");
        this.zoomInControlElm = this.domNode.querySelector(".map-controls__zoom-in");
        this.resetControlElm = this.domNode.querySelector(".map-controls__reset");
        this.locationResultsContainer = this.domNode.querySelector('.depot-finder__results');
        this.depotLocationTemplate = document.querySelector('#depot-location--template');

        //Google Map Libraries
        this.MapLib = MapLib;
        this.MarkerLib = MarkerLib;
        this.InfoWindowLib = InfoWindowLib;

        this.mapMarkers = [];
        this.mapWindows = [];
        this.locations = [];
        this.selectedSlug = '';
        this.zoom = 8;
        this.tilt = 30;
        this.lng = -3.975675;
        this.lat = 55.956775;

        this.iconBase = "../assets/img/svg/icons/";
        this.icons = {
            info: {
                icon: this.iconBase + "map-pin.svg",
            },
            current: {
                icon: this.iconBase + "map-pin--selected.svg",
            },
            user: {
                icon: this.iconBase + "map-pin--user.svg",
            }
        };

        const zoom = this.zoom;
        const tilt = this.tilt;
        const lat = this.lat;
        const lng = this.lng;
        this.map = new this.MapLib(this.mapElm, {
            center: {
                lat: lat,
                lng: lng
            },
            zoom: zoom,
            tilt: tilt,
            mapId: '1ae02ae600365ec8',
            streetViewControl: false,
            clickableIcons: false,
            mapTypeControl: false,
            fullscreenControl: false,
            disableDefaultUI: true,
            scrollwheel: false
        });

        this.zoomOutControlElm.addEventListener('click', this.handleMapZoomOut.bind(this), false);
        this.zoomInControlElm.addEventListener('click', this.handleMapZoomIn.bind(this), false);
        this.resetControlElm.addEventListener('click', this.handleMapReset.bind(this), false);

        this.locationResultsContainer.addEventListener('loaded', this.initDepotLocations.bind(this), false);

        this.loadMapLocations(this.dataUrl);
    }


    async loadMapLocations(url){
        const jsonres = await fetch(url);
        const jsonList = await jsonres.json();
        this.jsonList = jsonList;
        this.createDepotLocations(jsonList);
        this.createMapLocations(jsonList);
        this.initDepotLocations();
    }

    createMapLocations(locations){
        locations.map((location) => {
            const lat = parseFloat(location.lat);
            const lng = parseFloat(location.lng);

            const content = `
                <div class="gmap--info-window">
                    <h4>${location.title}</h4>
                    <div class="detail">
                        <span><i class="mph-icon--phone"></i></span>
                        <span><a href="tel:${location.tel}">${location.tel}</a></span>
                    </div>
                    <button class="button button--primary info-window-trigger info-window-trigger--js" data-slug="${location.slug}">
                        <span>View Full Details</span>
                    </button>
                </div>
            `;

            const locationObj = {
                "location_slug": location.slug,
                "position": new google.maps.LatLng(lat, lng),
                "type": location.type,
                "content": content
            };

            this.locations.push(locationObj);
        });

        this.buildLocateButton();
        this.createMapMarkers();
    }

    createMapMarkers(){
        const locations = this.locations;
        const updateSelectedDepot = this.updateSelectedDepot.bind(this);
        const jsonList = this.jsonList;
        const selectedDepotElm = this.selectedDepotElm;
        let selectedSlug = this.selectedSlug;

        for (let i = 0; i < locations.length; i++) {
            const map = this.map;
            const location = locations[i];
            const icons = this.icons;

            const marker = new this.MarkerLib(
                {
                    marker_slug: location.location_slug,
                    position: location.position,
                    icon: icons.info.icon,
                    map: map,
                }
            );
            this.mapMarkers.push(marker);

            const window = new this.InfoWindowLib({
                content: location.content,
                window_slug: location.location_slug,
            });

            google.maps.event.addListener(
                window,
                'domready',
                function(){
                    if(isSet(selectedDepotElm)){
                        selectedSlug = selectedDepotElm.dataset.slug;
                    }

                    const slug = this.anchor.marker_slug;
                    const updateSelectedDepotHandler = (e) => updateSelectedDepot(jsonList, slug, e);
                    const trigger = document.querySelector(".info-window-trigger--js");
                    const triggerSpan = trigger.querySelector('span');

                    if(selectedSlug === slug){
                        trigger.setAttribute("disabled","");
                        triggerSpan.textContent = 'Currently Viewing';
                    }else{
                        trigger.removeAttribute("disabled");
                        triggerSpan.textContent = 'View Full Details';
                        trigger.addEventListener('click', updateSelectedDepotHandler, false);
                    }
                }
            ); 

            this.mapWindows.push(window);

            const markerHandler = (e) => this.handleMapMarkerClick(marker, window, e);

            marker.addListener("click", markerHandler);
        }
    }

    createDepotLocations(locations){
        for (let i = 0; i < locations.length; i++) {
            let depotLocationElm = this.depotLocationTemplate.content.cloneNode(true).children[0];

            let location = locations[i];

            if(!isSet(location.slug)) return;

            let depotLocationElmTrigger = depotLocationElm.querySelector('.depot-location__map-trigger');
            let depotLocationElmTitle = depotLocationElm.querySelector('.depot-location__title');
            let depotLocationElmAddress = depotLocationElm.querySelector('.depot-location__address');
            let depotLocationElmPhone = depotLocationElm.querySelector('.depot-location__phone');

            depotLocationElm.dataset.slug = location.slug;

            if(isSet(location.title)){
                depotLocationElmTitle.textContent = location.title;
            } else{
                depotLocationElmTitle.remove();
            }

            if(isSet(location.address)){
                depotLocationElmAddress.textContent = location.address;
            } else{
                depotLocationElmAddress.remove();
            }

            if(isSet(location.tel)){
                depotLocationElmPhone.textContent = location.tel;
            } else{
                depotLocationElmPhone.remove();
            }

            this.locationResultsContainer.append(depotLocationElm);
        }
    }

    initDepotLocations(){
        const handleDepotOptionClick = this.handleDepotOptionClick.bind(this);
        const depotLocations = this.domNode.querySelectorAll('.depot-location--js');

        depotLocations.forEach(function (location) {
            const trigger = location.querySelector('.depot-location__map-trigger--js')
            trigger.addEventListener('click', handleDepotOptionClick, false);
        });

    }

    handleDepotOptionClick(e){
        const map = this.map;
        const icons = this.icons;
        const locations = this.domNode.querySelectorAll('.card--depot-location');
        const locationElm = e.target.closest('.card--depot-location');
        const buttonElm = e.target;
        const slug = locationElm.dataset.slug;

        locations.forEach(function (location) {
            location.classList.remove("is-selected");
            location.querySelector(".depot-location__map-trigger--js span").textContent = "View On Map";
            location.querySelector(".depot-location__map-trigger--js").removeAttribute('disabled');
        });

        locationElm.classList.add("is-selected");
        buttonElm.querySelector('span').textContent = "Selected";
        buttonElm.setAttribute('disabled', '');
        this.updateSelectedDepot(this.jsonList, slug);

        this.mapWindows.forEach(function (window){
            window.close();
        })

        this.mapMarkers.forEach(function (marker){
            marker.setIcon(icons.info.icon);

            if(marker.marker_slug === slug){
                map.panTo(marker.position);
                marker.setIcon(icons.current.icon);
                map.setZoom(12);
                map.setCenter(marker.position);
            }
        });
    }

    handleMapZoomIn(){
        const map = this.map;
        map.setZoom(map.getZoom() + 1);
    }

    handleMapZoomOut(){
        const map = this.map;
        map.setZoom(map.getZoom() - 1);
    }

    handleMapReset(){
        const position = new google.maps.LatLng(this.lat, this.lng);

        this.mapWindows.forEach(function (window){
            window.close();
        });

        const icons = this.icons;
        this.mapMarkers.forEach(function (marker){
            marker.setIcon(icons.info.icon);
        });

        this.map.setZoom(this.zoom);
        this.map.setTilt(this.tilt);
        this.map.panTo(position);
    }

    handleMapMarkerClick(marker, window, e){
        const map = this.map;
        const icons = this.icons;
        const markers = this.mapMarkers;
        const windows = this.mapWindows;

        for (let i = 0; i < windows.length; i++) {
            const window = windows[i];
            window.close();
        }

        for (let i = 0; i < markers.length; i++) {
            const marker = markers[i];
            marker.setIcon(icons.info.icon);
        }

        marker.setIcon(icons.current.icon);

        map.panTo(
            marker.position
        );

        window.open({
            anchor: marker,
            map,
            shouldFocus: false,
        });

        google.maps.event.addListener(map, 'click', function() {
            window.close();
            marker.open = false;
        });
    }

    buildLocateButton(){
        if (!navigator.geolocation) return;

        const button = document.createElement('button');
        button.classList.add('map-controls__locate', 'button', 'square');

        const icon = document.createElement('i');
        icon.classList.add('mph-icon--locate');

        button.append(icon);
        const mapControlsContainer = document.querySelector('.map-controls');

        button.addEventListener('click', this.handleLocateButtonClick.bind(this), false);

        mapControlsContainer.append(button);
    }

    getDistance(userLat, userLng, depotLat, depotLng, unit) {
        if ((userLat == depotLat) && (userLng == depotLng)) {
            return 0;
        } else {
            const radUserlat = Math.PI * userLat/180;
            const radDepotLat = Math.PI * depotLat/180;
            const theta = userLng-depotLng;
            const radTheta = Math.PI * theta/180;
            let dist = Math.sin(radUserlat) * Math.sin(radDepotLat) + Math.cos(radUserlat) * Math.cos(radDepotLat) * Math.cos(radTheta);
            
            if (dist > 1) {
                dist = 1;
            }

            dist = Math.acos(dist);
            dist = dist * 180/Math.PI;
            dist = dist * 60 * 1.1515;

            if (unit=="K") { 
                dist = dist * 1.609344 
            }

            if (unit=="N") { 
                dist = dist * 0.8684 
            }

            return dist;
        }
    }

    async getUserLocation() {
        const pos = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        });
      
        return {
            lng: pos.coords.longitude,
            lat: pos.coords.latitude,
        };
    }

    findClosestDepotLocation(userLocation){
        const locations = this.locations;
        const user = userLocation;
        let closestLocation = "";
        let closestLocationDistance = ""

        for (let i = 0; i < locations.length; i++) {
            let location = locations[i];

            let userLat = user.lat;
            let userLng = user.lng;
            let locationLat = location.position.lat();
            let locationLng = location.position.lng();

            let distance = this.getDistance(userLat, userLng, locationLat, locationLng);

            if(closestLocation == "" || closestLocationDistance == ""){
                closestLocation = location;
                closestLocationDistance = distance;
                continue;
            }

            if(distance < closestLocationDistance){
                closestLocation = location;
                closestLocationDistance = distance;
            }
        }

        this.closestLocation = closestLocation;
    }

    async handleLocateButtonClick(){
        if(!isSet(this.closestLocation)){
            const userLocation = await this.getUserLocation();
            this.userLocation = userLocation;

            this.findClosestDepotLocation(userLocation);
        }

        const map = this.map;
        const markers = this.mapMarkers;
        const windows = this.mapWindows;
        const icons = this.icons;
        const userLocation = this.userLocation;
        let closestWindow;
        let closestMarker;

        for (let i = 0; i < windows.length; i++) {
            const infoWindow = windows[i];
            infoWindow.close();
            
            if(this.closestLocation.location_slug == infoWindow.window_slug){
                closestWindow = infoWindow;
            }
        }

        for (let i = 0; i < markers.length; i++) {
            const marker = markers[i];
            marker.setIcon(icons.info.icon);

            if(this.closestLocation.location_slug == marker.marker_slug){
                closestMarker = marker;
            }
        }

        const userPosition = new google.maps.LatLng(userLocation.lat, userLocation.lng);
        const closestPosition = closestMarker.getPosition();

        if(isSet(this.userMarker)) this.userMarker.setMap(null);

        this.userMarker = new this.MarkerLib(
            {
                marker_slug: 'user',
                position: userPosition,
                icon: icons.user.icon,
                map: map,
            }
        );

        closestMarker.setIcon(icons.current.icon);

        let mapBounds = new google.maps.LatLngBounds();
        mapBounds.extend(userPosition);
        mapBounds.extend(closestPosition);
        map.fitBounds(mapBounds);
        map.setZoom(map.getZoom() - 0.5);

        google.maps.event.addListener(map, 'click', function() {
            closestWindow.close();
            closestMarker.open = false;
        });

        this.updateSelectedDepot(this.jsonList, this.closestLocation.location_slug);
    }

    updateSelectedDepot(json, slug, e){
        const depotLocation = document.querySelector('.depot-location--js[data-slug="' + slug + '"]');
        const buttonElm = depotLocation.querySelector('.depot-location__map-trigger--js');

        depotLocation.scrollIntoView({ behavior: "smooth", block: 'nearest', inline: 'start' });

        const locations = this.domNode.querySelectorAll('.card--depot-location');
        locations.forEach(function (location) {
            location.classList.remove("is-selected");
            location.querySelector(".depot-location__map-trigger--js span").textContent = "View On Map";
            location.querySelector(".depot-location__map-trigger--js").removeAttribute('disabled');
        });

        depotLocation.classList.add('is-selected');
        buttonElm.querySelector('span').textContent = "Selected";
        buttonElm.setAttribute('disabled', '');

        const windowTrigger = document.querySelector(".info-window-trigger--js");
        if(isSet(windowTrigger)){
            windowTrigger.setAttribute("disabled","");
            const windowTriggerSpan = windowTrigger.querySelector('span');
            windowTriggerSpan.textContent = 'Selected';
        }
    }
}

exports.DepotFinder = DepotFinder